import * as Yup from "yup";
import { useState } from "react";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { createCompanyApi } from "../../../services/super-admin-service/_company";
import { errorToast, successToast } from "../../../components/Toast";
import { useNavigate } from "react-router-dom";

export const convertFileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = () => {
      if (reader.result) {
        resolve((reader.result as string).split(",")[1]);
      } else {
        reject(new Error("File could not be read"));
      }
    };

    reader.onerror = (error) => reject(error);
  });
};

const CreateCompany = () => {
  const [addLoading, setAddLoading] = useState<boolean>(false);
  const navigate = useNavigate();

  const createCompanyValidationSchema = Yup.object().shape({
    adminName: Yup.string().required("Name is required."),
    adminEmail: Yup.string()
      .email("Invalid email")
      .required("Email is required."),
    adminPhone: Yup.string().optional(),
    password: Yup.string().required("Password is required."),
    companyName: Yup.string().required("Company name is required."),
    companyLogo: Yup.object().shape({
      fileName: Yup.string().required("Please select company logo."),
      fileData: Yup.string().required("Please select company logo."),
    }),
    companyLogoName: Yup.string().required("Company logo name is required."),
    invoiceHeader: Yup.object().shape({
      fileName: Yup.string().required("Please select invoice header."),
      fileData: Yup.string().required("Please select invoice header."),
    }),
  });

  const createCompanyInitialValues = {
    adminName: "",
    adminEmail: "",
    adminPhone: "",
    password: "",
    companyName: "",
    companyLogo: {
      fileName: "",
      fileData: "",
    },
    companyLogoName: "",
    invoiceHeader: {
      fileName: "",
      fileData: "",
    },
  };

  const handleSubmit = async (values: any) => {
    setAddLoading(true);

    // Prepare data for API
    const payload = {
      adminName: values.adminName,
      adminEmail: values.adminEmail,
      adminPhone: values.adminPhone,
      password: values.password,
      companyName: values.companyName,
      companyLogo: values.companyLogo,
      companyLogoName: values.companyLogoName,
      invoiceHeader: values.invoiceHeader,
    };

    try {
      // Call your API here
      const result: any = await createCompanyApi(payload);
      if (result.isSuccess) {
        successToast(result.message);
        navigate("/super-admin/company");
      } else {
        errorToast(result.message);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setAddLoading(false);
    }
  };

  return (
    <>
      <div className="card">
        <div className="card-header pb-0 d-flex justify-content-between">
          <div>
            <h5>Create Company</h5>
          </div>
        </div>

        <Formik
          initialValues={createCompanyInitialValues}
          validationSchema={createCompanyValidationSchema}
          onSubmit={handleSubmit}
        >
          {({ handleChange, values, setFieldValue }) => (
            <Form>
              {/* Admin User Details Section */}
              <div className="card-body pt-0">
                <div className="card my-card">
                  <div className="card-header my-card-header bg-lighter">
                    <div>
                      <h6 className="my-card-header-head">
                        Admin user Details
                      </h6>
                    </div>
                  </div>
                  <div className="card-body my-card-body">
                    <div className="dt_adv_search">
                      <div className="row g-3 m-0">
                        <div className="col-12 col-sm-6 col-lg-4">
                          <label
                            htmlFor="adminName"
                            className="form-label"
                            style={{ fontSize: "0.9375rem" }}
                          >
                            Name: <span className="text-danger">*</span>
                          </label>
                          <Field
                            id="adminName"
                            name="adminName"
                            type="text"
                            className="form-control"
                            placeholder="Enter name"
                          />
                          <ErrorMessage
                            name="adminName"
                            className="text-danger"
                            component="div"
                          />
                        </div>
                        <div className="col-12 col-sm-6 col-lg-4">
                          <label
                            htmlFor="adminEmail"
                            className="form-label"
                            style={{ fontSize: "0.9375rem" }}
                          >
                            Email:<span className="text-danger">*</span>
                          </label>
                          <Field
                            id="adminEmail"
                            name="adminEmail"
                            type="email"
                            className="form-control"
                            placeholder="Enter email"
                          />
                          <ErrorMessage
                            name="adminEmail"
                            className="text-danger"
                            component="div"
                          />
                        </div>
                        <div className="col-12 col-sm-6 col-lg-4">
                          <label
                            htmlFor="adminPhone"
                            className="form-label"
                            style={{ fontSize: "0.9375rem" }}
                          >
                            Phone No:
                          </label>
                          <Field
                            id="adminPhone"
                            name="adminPhone"
                            type="text"
                            className="form-control"
                            placeholder="Enter phone No"
                          />
                          <ErrorMessage
                            name="adminPhone"
                            className="text-danger"
                            component="div"
                          />
                        </div>
                        <div className="col-12 col-sm-6 col-lg-4">
                          <label
                            htmlFor="password"
                            className="form-label"
                            style={{ fontSize: "0.9375rem" }}
                          >
                            Password: <span className="text-danger">*</span>
                          </label>
                          <Field
                            id="password"
                            name="password"
                            type="password"
                            className="form-control"
                            placeholder="Enter password"
                          />
                          <ErrorMessage
                            name="password"
                            className="text-danger"
                            component="div"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Company Details Section */}
              <div className="card-body pt-0">
                <div className="card my-card">
                  <div className="card-header my-card-header bg-lighter">
                    <div>
                      <h6 className="my-card-header-head">Company Details</h6>
                    </div>
                  </div>
                  <div className="card-body my-card-body">
                    <div className="dt_adv_search">
                      <div className="row g-3 m-0">
                        <div className="col-12 col-sm-6 col-lg-4">
                          <label
                            htmlFor="companyName"
                            className="form-label"
                            style={{ fontSize: "0.9375rem" }}
                          >
                            Company Name: <span className="text-danger">*</span>
                          </label>
                          <Field
                            id="companyName"
                            name="companyName"
                            type="text"
                            className="form-control"
                            placeholder="Enter company name"
                          />
                          <ErrorMessage
                            name="companyName"
                            className="text-danger"
                            component="div"
                          />
                        </div>
                        <div className="col-12 col-sm-6 col-xl-4">
                          <label
                            className="form-label"
                            style={{ fontSize: "0.9375rem" }}
                          >
                            Select Company Logo:{" "}
                            <span className="text-danger">*</span>
                          </label>

                          <div
                            className="d-flex"
                            style={{ position: "relative" }}
                          >
                            <div
                              className="form-control border-end-0 rounded-0 rounded-start"
                              style={{
                                width: "auto",
                                whiteSpace: "nowrap",
                              }}
                            >
                              Choose Image
                            </div>
                            <div
                              className="form-control rounded-0 rounded-end"
                              style={{
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                                overflow: "hidden",
                              }}
                            >
                              {values.companyLogo.fileName.split("/").pop() ||
                                "No File Chosen"}
                            </div>

                            <input
                              type="file"
                              name="companyLogo"
                              id="companyLogo"
                              className="form-control"
                              style={{
                                position: "absolute",
                                top: 0,
                                opacity: 0,
                              }}
                              accept="image/*"
                              onChange={async (event: any) => {
                                const file = event.currentTarget.files[0];
                                if (file) {
                                  const base64FileData =
                                    await convertFileToBase64(file);
                                  const companyLogo = {
                                    fileName: file.name,
                                    fileData: base64FileData,
                                  };
                                  setFieldValue("companyLogo", companyLogo);
                                }
                              }}
                            />
                          </div>

                          <ErrorMessage
                            name="companyLogo.fileName"
                            className="text-danger"
                            component="div"
                          />
                        </div>
                        <div className="col-12 col-sm-6 col-lg-4">
                          <label
                            htmlFor="companyLogoName"
                            className="form-label"
                            style={{ fontSize: "0.9375rem" }}
                          >
                            Company Logo Name:
                            <span className="text-danger">*</span>
                          </label>
                          <Field
                            id="companyLogoName"
                            name="companyLogoName"
                            type="text"
                            className="form-control"
                            placeholder="Enter logo name"
                          />
                          <ErrorMessage
                            name="companyLogoName"
                            className="text-danger"
                            component="div"
                          />
                        </div>

                        <div className="col-12 col-sm-6 col-xl-4">
                          <label
                            className="form-label"
                            style={{ fontSize: "0.9375rem" }}
                          >
                            Invoice Header:{" "}
                            <span className="text-danger">*</span>
                          </label>

                          <div
                            className="d-flex"
                            style={{ position: "relative" }}
                          >
                            <div
                              className="form-control border-end-0 rounded-0 rounded-start"
                              style={{
                                width: "auto",
                                whiteSpace: "nowrap",
                              }}
                            >
                              Choose Image
                            </div>
                            <div
                              className="form-control rounded-0 rounded-end"
                              style={{
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                                overflow: "hidden",
                              }}
                            >
                              {values.invoiceHeader.fileName.split("/").pop() ||
                                "No File Chosen"}
                            </div>

                            <input
                              type="file"
                              name="invoiceHeader"
                              id="invoiceHeader"
                              className="form-control"
                              style={{
                                position: "absolute",
                                top: 0,
                                opacity: 0,
                              }}
                              accept="image/*"
                              onChange={async (event: any) => {
                                const file = event.currentTarget.files[0];
                                if (file) {
                                  const base64FileData =
                                    await convertFileToBase64(file);
                                  const invoiceHeader = {
                                    fileName: file.name,
                                    fileData: base64FileData,
                                  };
                                  setFieldValue("invoiceHeader", invoiceHeader);
                                }
                              }}
                            />
                          </div>

                          <ErrorMessage
                            name="invoiceHeader.fileName"
                            className="text-danger"
                            component="div"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="card-body pt-0">
                <div className="d-flex justify-content-start">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={addLoading}
                  >
                    {addLoading && (
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                    )}
                    Create Company
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
};

export default CreateCompany;
